/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject } from "@angular/core";
import { EChartsType } from "echarts";
import { defer, map, Subject } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ChartComponent } from "../../../../shared/components/chart/chart.component";
import { AdvancedFilters } from "../../../../shared/models/http/dashboard/advanced-filters.model";
import { SoftwareAssetsGlobalForm } from "../../../../shared/models/ui/software-assets-global-form.model";
import { AuthorizationService } from "../../../../shared/services/authorization.service";
import { DashboardComponentEmissionsService } from "../../../../shared/services/dashboard-component-emissions.service";
import { DashboardStateService } from "../../../../shared/services/state/dashboard-state.service";
import { COLORS } from "../../../../shared/utils/constants";
import { GRANTS } from "../../../../shared/utils/grants.constants";
import { lazyLoad } from "../../../../shared/utils/lazy-load";
import { formatDateForBE, saveAs } from "../../../../shared/utils/utils";

@Component({
	selector: "globalictcc-component-emissions",
	standalone: true,
	imports: [AsyncPipe, ChartComponent, ButtonComponent],
	templateUrl: "./component-emissions.component.html",
	styleUrl: "./component-emissions.component.scss"
})
export class ComponentEmissionsComponent {
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.swart;
	public resourcesEmissionsColors: string[] = [
		COLORS.TURQUOISE_100,
		COLORS.GREEN_50,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50
	];

	private readonly _embodiedEmissionsRefresh$ = new Subject<void>();
	private readonly _totalCo2EmissionsRefresh$ = new Subject<void>();
	private readonly _resourcesEmissionsRefresh$ = new Subject<void>();
	private readonly _filters$ = defer(
		() => this._dashboardStateService.softwareAssetsGlobalForm$
	).pipe(map(form => this._buildAdvancedFilters(form)));

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly embodiedEmissions$ = lazyLoad(
		this._filters$,
		filters => this._dashboardComponentEmissionsService.getEmbodiedEmissionsBarChart(filters),
		this._embodiedEmissionsRefresh$
	);

	public readonly totalCo2Emissions$ = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardComponentEmissionsService.getTotalCo2EmissionsForLineChart(filters),
		this._totalCo2EmissionsRefresh$
	);

	public readonly resourcesEmissions$ = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardComponentEmissionsService.getResourcesEmissionsPieChartData(filters),
		this._resourcesEmissionsRefresh$
	);

	constructor(
		private _dashboardComponentEmissionsService: DashboardComponentEmissionsService,
		private _dashboardStateService: DashboardStateService,
		public authorizationService: AuthorizationService
	) {}

	public onEmbodiedEmissionsChartRefresh() {
		this._embodiedEmissionsRefresh$.next();
	}

	public onTotalCo2EmissionsChartRefresh() {
		this._totalCo2EmissionsRefresh$.next();
	}

	public onResourcesEmissionsChartRefresh() {
		this._resourcesEmissionsRefresh$.next();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	private _buildAdvancedFilters(form: SoftwareAssetsGlobalForm): AdvancedFilters | undefined {
		const startDate = formatDateForBE(form.timePeriod?.start);
		const endDate = formatDateForBE(form.timePeriod?.end);
		if (!startDate || !endDate) return undefined;

		const { applicationsSelection, layersSelection } = form;
		return {
			startDate,
			endDate,
			allApplicationsSelected: applicationsSelection.allSelected,
			allLayersSelected: layersSelection.allSelected,
			applicationsSelection: {
				selectedIds: applicationsSelection.selectedIds,
				excludedIds: applicationsSelection.excludedIds
			},
			layersSelection: {
				selectedIds: layersSelection.selectedIds,
				excludedIds: layersSelection.excludedIds
			}
		};
	}
}
